import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { TaskList } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { useToast } from '../../context/ToastContext';
import { userName } from '../../utils/taskUsers';
import { PROJECT_MAX_DEPTH, TreeShape } from '../../utils/taskTree';
import ListCard, { LIST_COLORS } from '../tasks/ListCard';
import Popover from '../tasks/Popover';
import ShareDialog from '../tasks/ShareDialog';
import TaskDialog from '../tasks/TaskDialog';
import TaskIcon, { TaskIconName } from '../tasks/TaskIcon';
import { UserBubble } from '../tasks/UserBubble';
import LoadingSpinner from '../LoadingSpinner';
import MindMap from './map/MindMap';
import GanttView from './gantt/GanttView';
import MaterialsView from './MaterialsView';
import ValueView from './ValueView';
import ProjectDashboard from './dashboard/ProjectDashboard';
import { MapMode } from './map/layout';
import { inDaysLabel, lateCount, nextMarker, projectProgress } from './projectStats';
import { useCollapsed } from './useCollapsed';
import '../../tasks.css';
import '../../projects.css';

type ProjectView = 'overview' | 'map' | 'outline' | 'gantt' | 'materials' | 'value';

const VIEWS: { value: ProjectView; label: string; icon: TaskIconName }[] = [
  { value: 'overview', label: 'Overview', icon: 'bell' },
  { value: 'map', label: 'Map', icon: 'project' },
  { value: 'outline', label: 'Outline', icon: 'list' },
  { value: 'gantt', label: 'Gantt', icon: 'gantt' },
  { value: 'materials', label: 'Materials', icon: 'cart' },
  { value: 'value', label: 'Value', icon: 'value' },
];

const OUTLINE_SHAPE: TreeShape = { maxDepth: PROJECT_MAX_DEPTH, rootParent: null, depthOffset: 0 };

function readPref<T extends string>(key: string, allowed: T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}
function writePref(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* per-viewer convenience only */
  }
}

const ProjectPage: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const {
    ready, error, me, users, today, lists, itemsForList, members, canEditList, updateList, deleteList, showOnLists,
    lastError, clearError,
  } = useTasks();

  const project = lists.find((l) => l.id === Number(id) && l.kind === 'project');
  const items = useMemo(() => (project ? itemsForList(project.id) : []), [project, itemsForList]);
  const projectMembers = project ? members.get(project.id) || [] : [];
  const isOwner = project?.owner_email === me;
  const readOnly = !project || !canEditList(project);

  const [view, setViewState] = useState<ProjectView>(() => readPref('project.view', ['overview', 'map', 'outline', 'gantt', 'materials', 'value'], 'overview'));
  const [mode, setModeState] = useState<MapMode>(() => readPref('project.mapMode', ['map', 'logic'], 'map'));
  const setView = (v: ProjectView) => {
    setViewState(v);
    writePref('project.view', v);
  };
  const setMode = (m: MapMode) => {
    setModeState(m);
    writePref('project.mapMode', m);
  };

  const [search, setSearch] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  const [sharing, setSharing] = useState<TaskList | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const menuRef = useRef<HTMLButtonElement>(null);

  // Deep link: /projects/12?item=345 (week-list chips, My Tasks, emails).
  const params = new URLSearchParams(location.search);
  const focusItemId = Number(params.get('item')) || null;
  const isNew = params.get('new') === '1';

  // A link to one topic shows it on the map, from tabs that can't show it in place.
  useEffect(() => {
    if (focusItemId && (view === 'overview' || view === 'materials' || view === 'value')) setView('map');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusItemId]);

  // ── Title ──────────────────────────────────────────────────────────────────
  const [title, setTitle] = useState(project?.title || '');
  const titleFocused = useRef(false);
  useEffect(() => {
    if (!titleFocused.current) setTitle(project?.title || '');
  }, [project?.title]);
  const commitTitle = () => {
    titleFocused.current = false;
    const next = title.trim();
    if (project && next !== project.title) updateList(project.id, { title: next });
  };

  const progress = useMemo(() => projectProgress(items), [items]);
  const fold = useCollapsed(project?.id ?? 0, items);
  const next = useMemo(() => nextMarker(items, today), [items, today]);
  const late = useMemo(() => lateCount(items), [items]);
  const myBoard = project
    ? lists.find(
        (l) => l.kind === 'view' && l.view_list_id === project.id && !l.view_root_item_id &&
          (l.owner_email === me || (members.get(l.id) || []).includes(me))
      )
    : undefined;

  if (!ready) {
    return error ? (
      <div className="tk-page"><div className="tk-setup"><h2>Projects aren't available yet</h2><p>{error}</p></div></div>
    ) : (
      <LoadingSpinner />
    );
  }

  if (!project) {
    return (
      <div className="tk-page">
        <Link to="/projects" className="tp-back"><TaskIcon name="chevron-left" size={15} /> Projects</Link>
        <div className="tk-setup">
          <h2>Project not found</h2>
          <p className="tk-muted">It may have been archived or deleted, or it hasn't been shared with you.</p>
          <Link to="/projects" className="tk-btn tk-btn-primary">All projects</Link>
        </div>
      </div>
    );
  }

  const people = [project.owner_email, ...projectMembers.filter((m) => m !== project.owner_email)];

  return (
    <div className="tk-page tp-page">
      <Link to="/projects" className="tp-back"><TaskIcon name="chevron-left" size={15} /> Projects</Link>

      <header className="tk-page-head tp-head">
        <div className="tp-head-title">
          <span className="tp-head-icon" style={project.color ? ({ '--tl-tint': project.color } as React.CSSProperties) : undefined}>
            <TaskIcon name="project" size={18} />
          </span>
          <div className="tp-head-text">
            {readOnly ? (
              <h1 className="tk-page-title">{project.title || 'Untitled project'}</h1>
            ) : (
              <input
                className="tk-page-title tp-title-input"
                value={title}
                placeholder="Project name"
                onFocus={() => (titleFocused.current = true)}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                autoFocus={isNew}
                aria-label="Project name"
              />
            )}
            <div className="tp-head-meta">
              <div className="tl-progress tp-head-progress" title={`${progress}% done`}>
                <div className="tl-progress-bar"><span style={{ width: `${progress}%` }} /></div>
                <span className="tl-progress-text">{progress}%</span>
              </div>
              {next && (
                <button
                  type="button"
                  className="tk-chip tk-chip-week"
                  onClick={() => navigate(`/projects/${project.id}?item=${next.item.id}`, { replace: true })}
                  title="Next event or milestone"
                >
                  <TaskIcon name={next.item.node_kind === 'event' ? 'event' : 'milestone'} size={11} />
                  {next.item.text || 'Untitled'} {inDaysLabel(next.inDays)}
                </button>
              )}
              {late > 0 && (
                <button type="button" className="tk-chip tk-chip-danger" onClick={() => setView('gantt')} title="Tasks that can't be done in time as planned">
                  <TaskIcon name="critical" size={11} />
                  {late} late
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="tk-page-actions">
          <span className="tl-members" title={people.map((m) => userName(users, m)).join(', ')}>
            {people.slice(0, 5).map((m) => (
              <UserBubble key={m} users={users} email={m} title="" />
            ))}
            {people.length > 5 && <span className="tl-members-more">+{people.length - 5}</span>}
          </span>
          {isOwner && (
            <button type="button" className="tk-btn tk-btn-ghost" onClick={() => setSharing(project)} title="Share with people">
              <TaskIcon name="share" size={15} /> <span className="tk-hide-sm">Share</span>
            </button>
          )}
          <button
            type="button"
            className="tk-btn tk-btn-ghost"
            onClick={async () => {
              if (myBoard) navigate(`/lists?list=${myBoard.id}`);
              else if (await showOnLists(project.id, null)) showToast('The whole project is on your Lists', 'success');
            }}
            title={myBoard ? 'Open its card on your Lists' : 'Show the whole project as a card on your Lists'}
          >
            <TaskIcon name="list" size={15} /> <span className="tk-hide-sm">{myBoard ? 'On your Lists' : 'Show on my Lists'}</span>
          </button>
          {!readOnly && (
            <button ref={menuRef} type="button" className="tk-icon-btn" onClick={() => setMenuOpen(true)} aria-label="Project options">
              <TaskIcon name="more" />
            </button>
          )}
        </div>
      </header>

      <div className="tk-toolbar">
        <div className="tk-segmented" role="group" aria-label="View">
          {VIEWS.map((v) => (
            <button key={v.value} type="button" className={view === v.value ? 'is-active' : ''} onClick={() => setView(v.value)} title={v.label}>
              <TaskIcon name={v.icon} size={14} /> <span className="tk-hide-sm">{v.label}</span>
            </button>
          ))}
        </div>
        {view === 'overview' || view === 'gantt' || view === 'materials' || view === 'value' ? null : view === 'map' ? (
          <div className="tk-segmented tk-segmented-sm" role="group" aria-label="Map layout">
            <button type="button" className={mode === 'map' ? 'is-active' : ''} onClick={() => setMode('map')} title="Branches on both sides">Balanced</button>
            <button type="button" className={mode === 'logic' ? 'is-active' : ''} onClick={() => setMode('logic')} title="Everything grows to the right">Right</button>
          </div>
        ) : (
          <>
            <label className="tk-search">
              <TaskIcon name="search" size={15} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search this project" aria-label="Search this project" />
              {search && (
                <button type="button" className="tk-icon-btn tk-icon-btn-sm" onClick={() => setSearch('')} aria-label="Clear search">
                  <TaskIcon name="close" size={13} />
                </button>
              )}
            </label>
            <div className="tk-segmented" role="group" aria-label="Filter">
              <button type="button" className={!mineOnly ? 'is-active' : ''} onClick={() => setMineOnly(false)}>Everything</button>
              <button type="button" className={mineOnly ? 'is-active' : ''} onClick={() => setMineOnly(true)}>Assigned to me</button>
            </div>
          </>
        )}
      </div>

      {lastError && (
        <div className="tk-error-bar" role="alert">
          <span>{lastError}</span>
          <button type="button" className="tk-icon-btn tk-icon-btn-sm" onClick={clearError} aria-label="Dismiss"><TaskIcon name="close" size={13} /></button>
        </div>
      )}

      {view === 'overview' ? (
        <ProjectDashboard
          projects={[project]}
          items={items}
          readOnly={readOnly}
          onOpenItem={(item) => {
            setView('map');
            navigate(`/projects/${project.id}?item=${item.id}`, { replace: true });
          }}
        />
      ) : view === 'materials' ? (
        <MaterialsView project={project} items={items} readOnly={readOnly} />
      ) : view === 'value' ? (
        <ValueView
          items={items}
          readOnly={readOnly}
          onOpenItem={(itemId) => {
            setView('map');
            navigate(`/projects/${project.id}?item=${itemId}`, { replace: true });
          }}
        />
      ) : view === 'gantt' ? (
        <GanttView key={project.id} project={project} items={items} readOnly={readOnly} focusItemId={focusItemId} fold={fold} />
      ) : view === 'map' ? (
        <MindMap key={project.id} project={project} items={items} readOnly={readOnly} mode={mode} focusItemId={focusItemId} fold={fold} />
      ) : (
        <div className="tp-outline">
          <ListCard
            bare
            list={project}
            items={items}
            members={projectMembers}
            readOnly={readOnly}
            shape={OUTLINE_SHAPE}
            collapsed={fold.collapsed}
            onToggleFold={fold.toggle}
            highlightIds={new Set()}
            flashId={focusItemId}
            mineOnly={mineOnly}
            search={search}
            onOpenImage={setLightbox}
            onShare={setSharing}
            onDelete={() => setConfirmDelete(true)}
          />
        </div>
      )}

      {menuOpen && (
        <Popover anchor={menuRef.current} onClose={() => setMenuOpen(false)} label="Project options" width={250}>
          <div className="tk-menu">
            <div className="tk-menu-title">Colour</div>
            <div className="tl-swatches">
              {LIST_COLORS.map((c) => (
                <button
                  key={c.label}
                  type="button"
                  className={`tl-swatch${(project.color || null) === c.value ? ' is-selected' : ''}`}
                  style={{ background: c.value || 'var(--color-surface)' }}
                  title={c.label}
                  aria-label={c.label}
                  onClick={() => updateList(project.id, { color: c.value })}
                />
              ))}
            </div>
            {isOwner && (
              <>
                <div className="tk-menu-sep" />
                <button type="button" className="tk-menu-item" onClick={() => { setMenuOpen(false); setSharing(project); }}>
                  <TaskIcon name="share" size={15} /> Share
                </button>
                <button
                  type="button"
                  className="tk-menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    updateList(project.id, { archived: true });
                    navigate('/projects');
                  }}
                >
                  <TaskIcon name="archive" size={15} /> Archive
                </button>
                <button type="button" className="tk-menu-item tk-menu-danger" onClick={() => { setMenuOpen(false); setConfirmDelete(true); }}>
                  <TaskIcon name="trash" size={15} /> Delete project
                </button>
              </>
            )}
          </div>
        </Popover>
      )}

      {sharing && <ShareDialog list={sharing} members={members.get(sharing.id) || []} onClose={() => setSharing(null)} />}

      {confirmDelete && (
        <TaskDialog
          title={`Delete "${project.title || 'Untitled project'}"?`}
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <button type="button" className="tk-btn tk-btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button
                type="button"
                className="tk-btn tk-btn-danger"
                onClick={() => {
                  deleteList(project.id);
                  setConfirmDelete(false);
                  navigate('/projects');
                }}
              >
                Delete
              </button>
            </>
          }
        >
          <p>The whole map and every Lists card made from it are removed for everyone it's shared with.</p>
        </TaskDialog>
      )}

      {lightbox &&
        createPortal(
          <div className="tk-lightbox" onClick={() => setLightbox(null)} role="dialog" aria-label="Photo">
            <img src={lightbox} alt="" />
          </div>,
          document.body
        )}
    </div>
  );
};

export default ProjectPage;
